import React from 'react'

import styled from '@emotion/styled'
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import {
  FolderIcon as FolderClosedIcon,
  FolderOpenIcon,
  FileIcon,
  FileClock,
  FileCheck2,
  LinkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FileDownIcon,
  FolderDownIcon,
  FolderOpenDotIcon,
  CloudDownloadIcon,
  ExternalLinkIcon,
  FolderCogIcon,
  MoreVertical,
} from 'lucide-react'
import { NodeApi, NodeRendererProps } from 'react-arborist'

import theme from 'src/theme'

import { TreeViewNode, useFileTree } from '.'
import { Link } from '@cedarjs/router'

export const StyledTreeNodeBase = styled.div`
  padding: 2px 8px;
  display: flex;
  cursor: pointer;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
  &.active-node {
    background-color: rgba(5, 115, 237, 0.1);
    &:hover {
      background-color: rgba(5, 115, 237, 0.2);
    }
  }
`

const StyledTreeNode = styled(StyledTreeNodeBase)`
  align-items: center;
  min-width: max-content;
  border-left: 1px dashed ${alpha(theme.palette.text.primary, 0.4)};
`

const useNodeIcon = (node: NodeApi<TreeViewNode>) => {
  switch (node.data.fileType) {
    case 'file': {
      if (node.data.file?.file?.r2rIngestionStatus === 'PENDING') {
        return () => <FileClock fill="#D7BBA8" size={12} />
      }
      if (node.data.file?.file?.r2rIngestionStatus === 'SUCCESS') {
        return () => <FileCheck2 fill="#D0F0C0" size={12} />
      }
      return () => <FileIcon fill="#D7BBA8" size={12} />
    }
    case 'imported-file':
      // changed color from #D7BBA8 to #D0F0C0 to indicate the file has been ingested
      // into R2R as the assumption is that they will be ingested into R2R via the script
      return () => <FileDownIcon fill="#D0F0C0" size={12} />
    case 'link':
      return () => <LinkIcon fill="#82B9F6" size={12} />
    case 'imported-root':
      return () => <CloudDownloadIcon fill="#B0BEC5" size={12} />
    case 'folder': {
      // system folder styling
      if (['STATIC', 'DYNAMIC'].includes(node.data.folderType)) {
        if (node.isOpen) {
          return () => <FolderCogIcon size={12} />
        }
        return () => <FolderCogIcon fill="#82B9F6" size={12} />
      }
      if (node.isOpen) {
        return () => <FolderOpenIcon size={12} />
      }
      return () => <FolderClosedIcon fill="" size={12} />
    }
    case 'imported-folder': {
      if (node.isOpen) {
        return () => <FolderOpenDotIcon size={12} />
      }
      return () => <FolderDownIcon fill="#A3C19A" size={12} />
    }
    default:
      return () => <FolderClosedIcon fill="" size={12} />
  }
}

interface CustomNodeProps extends NodeRendererProps<TreeViewNode> {
  onContextMenu?: (event: React.MouseEvent) => void
}

const CustomNode: React.FC<CustomNodeProps> = ({
  node,
  style,
  dragHandle,
  onContextMenu,
}) => {
  const { data } = node
  const { activeNode, activeNodeDataLoading, setActiveNode } = useFileTree()

  const Icon = useNodeIcon(node)
  const childrenLoading = activeNodeDataLoading && activeNode?.id === data.id

  const handleClick = () => {
    if (childrenLoading) return
    if (node.isEditing) return
    node.toggle()
    setActiveNode(node)
  }
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleClick()
    }
  }

  const getActiveClass = () => {
    if (activeNode && activeNode.id === data.id) {
      return 'active-node'
    }
    return ''
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    if (node.data.folderType === 'CUSTOM' || node.data.fileType === 'file') {
      setActiveNode(node)
      onContextMenu(event)
    }
  }
  return (
    <StyledTreeNode
      ref={dragHandle}
      className={`${getActiveClass()} group`}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        marginLeft: Number(style.paddingLeft) - 18,
        paddingLeft: 18,
        cursor: childrenLoading ? 'not-allowed' : 'pointer',
      }}
      onContextMenu={handleContextMenu}
    >
      <div
        className="flex w-full items-center gap-1"
        style={{ opacity: childrenLoading ? 0.5 : 1 }}
      >
        {node.isLeaf ? (
          <div style={{ width: 12 }} />
        ) : node.isOpen ? (
          <ChevronDownIcon size={12} />
        ) : (
          <ChevronRightIcon size={12} />
        )}
        <Tooltip
          title={
            node.data.file?.file?.r2rIngestionStatus
              ? `AI ingestion ${node.data.file?.file?.r2rIngestionStatus}`
              : ''
          }
        >
          <Icon />
        </Tooltip>
        {node.isEditing ? (
          <input
            autoFocus
            autoCorrect="off"
            autoComplete="off"
            spellCheck="false"
            type="text"
            className="w-full pr-2"
            defaultValue={node.data.name}
            onBlur={() => node.reset()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                node.reset()
              }
              if (e.key === 'Enter') {
                node.submit(e.currentTarget.value)
              }
            }}
          />
        ) : (
          <Typography variant="body2" className="pr-2">
            {data.name}
          </Typography>
        )}
      </div>
      {childrenLoading && <CircularProgress size={12} thickness={5} />}
      <div className="ml-auto flex h-[20px] items-center overflow-visible">
        {(node.data.fileType === 'file' ||
          node.data.folderType === 'CUSTOM') && (
          <Tooltip title="Options">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleContextMenu(e)
              }}
              className="opacity-0 transition-opacity group-hover:opacity-100"
            >
              <MoreVertical size={16} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {data?.payload?.targetLink && (
        <IconButton
          size="small"
          style={{ marginLeft: 'auto' }}
          component={Link}
          to={data.payload.targetLink}
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLinkIcon size={16} />
        </IconButton>
      )}
    </StyledTreeNode>
  )
}

export default CustomNode
